"use client";

import { useFeatureFlagEnabled } from "posthog-js/react";

/**
 * Central registry of PostHog feature flags used by the web app. Every flag
 * has a PostHog key and a MANDATORY code default. The default rules whenever
 * PostHog hasn't answered yet (first render), is blocked (ad blockers), is
 * quota-limited, or the flag doesn't exist in the project — so a missing or
 * unreachable flag always degrades to current behavior, never breakage.
 *
 * The registry doubles as the flag documentation: owner, intent, and cleanup
 * criteria live on each entry; deleting the entry is the cleanup. See
 * apps/web/docs/POSTHOG_FEATURE_FLAGS.md for the wider rules (and remember
 * flags must be created in BOTH PostHog projects — dev and prod).
 */
export const FEATURE_FLAGS = {
  /**
   * Kill switch for auto-navigating from dashboard and landing upload forms to
   * the single created job (dashboard shipped 2026-07-02; landing 2026-08-21).
   * Owner: viktor. Cleanup: remove once both journeys have proven themselves
   * (no complaints, activation steady).
   */
  uploadAutoNavigate: { key: "upload-auto-navigate", default: true },
  /**
   * Audio-only visual renders (waveform + background image) in the subtitle
   * editor preview and exports. PostHog is the single control: dev project
   * serves 100% (local/dev behavior), prod serves targeted users only.
   * Default false = off when PostHog is unreachable. Owner: viktor. Cleanup:
   * fold into the default experience (drop the flag) when visuals graduate.
   */
  audioVisuals: { key: "audio-visuals", default: false },
} as const;

export const MULTIVARIATE_FEATURE_FLAGS = {
  /**
   * Homepage experiment comparing the established transcription form with a
   * focused sign-up CTA. This visible layout choice has a bounded resolver in
   * HomepageHeroEntryExperiment instead of using the boolean-default hook below.
   * Owner: viktor. Cleanup: choose a permanent entry after the experiment,
   * then remove the boundary, attribution bridge, and this registry entry.
   */
  homepageTranscriptionEntry: {
    key: "homepage-transcription-entry",
    default: "control",
    variants: ["control", "cta"],
  },
} as const;

export type FeatureFlagName = keyof typeof FEATURE_FLAGS;

/**
 * Read a feature flag with its registry default. `useFeatureFlagEnabled`
 * returns undefined until the PostHog /flags response arrives (and forever if
 * it never does) — the default covers that window, so gate only enhancements
 * whose late flip is acceptable, and keep defaults matching the established
 * behavior.
 */
export function useFeatureFlag(name: FeatureFlagName): boolean {
  const { key, default: fallback } = FEATURE_FLAGS[name];
  const enabled = useFeatureFlagEnabled(key);
  return enabled ?? fallback;
}
